import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Grid, IconButton, Menu, MenuItem, Typography, styled } from '@mui/material';
import { isDefined } from 'lib/typeguards';
import * as React from 'react';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { getZonesArrayFromString, getZonesString, isExpandedLocationZone } from '../helpers';
import { CreateTableZoneModal, EditTableZoneModal, ITableZoneForm } from '../modal/TableZoneModal';
import { ILocationZoneExpanded, ILocationZones } from '../models/LocationModel';

interface ILocationTablesAndZonesCardEditComponentProps {
    zones: ILocationZones;
    onClose: () => void;
    isLoading: boolean;
    onSubmit: (zones: ILocationZones) => void;
    isZonalOrComtrex: boolean;
}

const StyledBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
}));

export const LocationTablesAndZonesCardEditComponent: React.FC<
    ILocationTablesAndZonesCardEditComponentProps
> = ({ zones, onClose, isLoading, onSubmit, isZonalOrComtrex }) => {
    const [stateZones, setStateZones] = React.useState(zones);
    const [isAddModalOpen, setAddModalVisibility] = React.useState(false);
    const [editingZoneModalDetails, setEditingZoneModalDetails] = React.useState<ITableZoneForm>();
    const [menuAnchorElements, setMenuAnchorElements] = React.useState<Record<string, HTMLButtonElement>>({});

    const handleZoneAddition = React.useCallback(
        ({
            zoneId,
            tables,
            salesAreaId,
            salesAreaIdItems,
            salesAreaIdDiscounts,
            salesAreaIdPayments
        }: ITableZoneForm) => {
            const zonalSettingsExist =
                isDefined(salesAreaId) ||
                isDefined(salesAreaIdItems) ||
                isDefined(salesAreaIdDiscounts) ||
                isDefined(salesAreaIdPayments);
            setStateZones(oldValues => ({
                ...oldValues,
                [zoneId]: {
                    tables: getZonesArrayFromString(tables),
                    ...(isZonalOrComtrex &&
                        zonalSettingsExist && {
                            zonalAztec: {
                                salesAreaId: Number(salesAreaId) || undefined,
                                salesAreaIdItems: Number(salesAreaIdItems) || undefined,
                                salesAreaIdDiscounts: Number(salesAreaIdDiscounts) || undefined,
                                salesAreaIdPayments: Number(salesAreaIdPayments) || undefined
                            }
                        })
                }
            }));
            setAddModalVisibility(false);
        },
        [setAddModalVisibility, setStateZones, isZonalOrComtrex]
    );

    const handleAddZoneClick = React.useCallback(() => {
        setAddModalVisibility(true);
    }, [setAddModalVisibility]);

    const handleAddTableZoneModalClose = React.useCallback(() => {
        setAddModalVisibility(false);
    }, [setAddModalVisibility]);

    const handleEditTableZoneModalClose = React.useCallback(() => {
        setEditingZoneModalDetails(undefined);
    }, [setEditingZoneModalDetails]);

    const handleZoneEdit = React.useCallback(
        ({
            zoneId,
            tables,
            salesAreaId,
            salesAreaIdItems,
            salesAreaIdDiscounts,
            salesAreaIdPayments
        }: ITableZoneForm) => {
            setStateZones(oldZones => {
                const newState = { ...oldZones };
                delete newState[editingZoneModalDetails.zoneId];
                const zonalSettingsExist =
                    isDefined(salesAreaId) ||
                    isDefined(salesAreaIdItems) ||
                    isDefined(salesAreaIdDiscounts) ||
                    isDefined(salesAreaIdPayments);
                newState[zoneId] = {
                    tables: getZonesArrayFromString(tables),
                    ...(isZonalOrComtrex &&
                        zonalSettingsExist && {
                            zonalAztec: {
                                salesAreaId: Number(salesAreaId) || undefined,
                                salesAreaIdItems: Number(salesAreaIdItems) || undefined,
                                salesAreaIdDiscounts: Number(salesAreaIdDiscounts) || undefined,
                                salesAreaIdPayments: Number(salesAreaIdPayments) || undefined
                            }
                        })
                };

                return newState;
            });

            setEditingZoneModalDetails(undefined);
        },
        [editingZoneModalDetails, isZonalOrComtrex]
    );

    const makeEditMenuOpenHandler = React.useCallback(
        (zoneId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
            setMenuAnchorElements(oldElements => ({
                ...oldElements,
                [zoneId]: event.currentTarget
            }));
        },
        [setMenuAnchorElements]
    );

    const removeMenuAnchorElement = React.useCallback(
        (zoneId: string) => {
            setMenuAnchorElements(oldElements => ({ ...oldElements, [zoneId]: null }));
        },
        [setMenuAnchorElements]
    );

    const makeEditZoneClickHandler = React.useCallback(
        (zoneId: string) => () => {
            removeMenuAnchorElement(zoneId);
            const zone = stateZones[zoneId];
            const tables = getZonesString(zone, false);
            const isExpanded = isExpandedLocationZone(stateZones[zoneId]);
            if (isExpanded && 'zonalAztec' in zone) {
                const zonalAztec = zone.zonalAztec;
                setEditingZoneModalDetails({
                    zoneId,
                    tables,
                    salesAreaId: zonalAztec?.salesAreaId ? Number(zonalAztec?.salesAreaId) : undefined,
                    salesAreaIdItems: zonalAztec?.salesAreaIdItems
                        ? Number(zonalAztec?.salesAreaIdItems)
                        : undefined,
                    salesAreaIdDiscounts: zonalAztec?.salesAreaIdDiscounts
                        ? Number(zonalAztec?.salesAreaIdDiscounts)
                        : undefined,
                    salesAreaIdPayments: zonalAztec?.salesAreaIdPayments
                        ? Number(zonalAztec?.salesAreaIdPayments)
                        : undefined
                });
            } else {
                setEditingZoneModalDetails({
                    zoneId,
                    tables
                });
            }
        },
        [stateZones, setEditingZoneModalDetails, removeMenuAnchorElement]
    );

    const makeEditMenuCloseHandler = React.useCallback(
        (zoneId: string) => () => {
            removeMenuAnchorElement(zoneId);
        },
        [removeMenuAnchorElement]
    );

    const makeZoneDeleteHandler = React.useCallback(
        (key: string) => () => {
            removeMenuAnchorElement(key);
            setStateZones(oldZones => {
                const newState = { ...oldZones };
                delete newState[key];

                return newState;
            });
        },
        [setStateZones, removeMenuAnchorElement]
    );

    const handleSubmit = React.useCallback(() => {
        onSubmit(stateZones);
    }, [onSubmit, stateZones]);

    const renderZoneRow = React.useCallback(
        (zoneKey: string) => {
            const editClickHandler = makeEditZoneClickHandler(zoneKey);
            const deleteClickHandler = makeZoneDeleteHandler(zoneKey);
            const editMenuOpenHandler = makeEditMenuOpenHandler(zoneKey);
            const editMenuCloseHandler = makeEditMenuCloseHandler(zoneKey);

            const zonalAztec =
                isZonalOrComtrex && isExpandedLocationZone(stateZones[zoneKey])
                    ? (stateZones[zoneKey] as ILocationZoneExpanded)?.zonalAztec
                    : undefined;

            return (
                <StyledBox key={zoneKey}>
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography>
                            <b>{zoneKey}</b>
                        </Typography>
                        <Box>
                            <IconButton
                                aria-haspopup="true"
                                aria-controls={`edit-menu-${zoneKey}`}
                                onClick={editMenuOpenHandler}
                            >
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                open={!!menuAnchorElements[zoneKey]}
                                id={`edit-menu-${zoneKey}`}
                                keepMounted={false}
                                anchorEl={menuAnchorElements[zoneKey]}
                                onClose={editMenuCloseHandler}
                            >
                                <MenuItem onClick={editClickHandler}>Edit</MenuItem>
                                <MenuItem onClick={deleteClickHandler}>Delete</MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                    <Typography variant="body2">
                        Table Numbers: {getZonesString(stateZones[zoneKey])}
                    </Typography>
                    {zonalAztec && (
                        <>
                            {zonalAztec?.salesAreaId && (
                                <Typography variant="body2">
                                    Sales Area ID: {zonalAztec.salesAreaId}
                                </Typography>
                            )}
                            {zonalAztec?.salesAreaIdItems && (
                                <Typography variant="body2">
                                    Sales Area ID Items: {zonalAztec.salesAreaIdItems}
                                </Typography>
                            )}
                            {zonalAztec?.salesAreaIdDiscounts && (
                                <Typography variant="body2">
                                    Sales Area ID Discounts: {zonalAztec.salesAreaIdDiscounts}
                                </Typography>
                            )}
                            {zonalAztec?.salesAreaIdPayments && (
                                <Typography variant="body2">
                                    Sales Area ID Payments: {zonalAztec.salesAreaIdPayments}
                                </Typography>
                            )}
                        </>
                    )}
                </StyledBox>
            );
        },
        [
            makeEditZoneClickHandler,
            makeZoneDeleteHandler,
            makeEditMenuOpenHandler,
            makeEditMenuCloseHandler,
            stateZones,
            menuAnchorElements,
            isZonalOrComtrex
        ]
    );

    const zoneKeys = React.useMemo(() => Object.keys(stateZones ?? {}), [stateZones]);

    return (
        <>
            <Grid container>
                {!!zoneKeys.length && (
                    <Grid item xs={12}>
                        {zoneKeys.map(renderZoneRow)}
                    </Grid>
                )}
                <Box width="100%" display="flex" marginTop={2}>
                    <Button variant="outlined" onClick={handleAddZoneClick}>
                        <AddIcon />
                    </Button>
                </Box>
                <Box width="100%" marginTop={2} display="flex" justifyContent="flex-end" alignItems="center">
                    <Box marginRight={2}>
                        <Button variant="outlined" onClick={onClose}>
                            Cancel
                        </Button>
                    </Box>

                    <LoadingButton
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                    >
                        Submit
                    </LoadingButton>
                </Box>
            </Grid>
            {isAddModalOpen && (
                <CreateTableZoneModal
                    onClose={handleAddTableZoneModalClose}
                    isZonalOrComtrex={isZonalOrComtrex}
                    open={isAddModalOpen}
                    onAddition={handleZoneAddition}
                    title="Create a new zone"
                />
            )}
            {!!editingZoneModalDetails && (
                <EditTableZoneModal
                    initialValues={editingZoneModalDetails}
                    isZonalOrComtrex={isZonalOrComtrex}
                    onEdit={handleZoneEdit}
                    onClose={handleEditTableZoneModalClose}
                    open={!!editingZoneModalDetails}
                />
            )}
        </>
    );
};
